import { useCallback, useEffect, useRef, useState } from 'react';
import { generalPriceRows, tirePriceTables } from '@/content/et/pricing';
import { site } from '@/content/et/site';
import styles from './PriceTable.module.css';

type PriceRow = { title: string; price: string };

type PriceCard = {
  id: string;
  label: string;
  tags: string[];
  rows: PriceRow[];
};

export function PriceTable() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const generalRows = generalPriceRows
    .filter((r) => !r.title.toLowerCase().startsWith('rehvivahetus'))
    .map((r) => ({
      title: r.title,
      price: r.price,
    }));

  const passengerRows = [
    ...tirePriceTables.passenger.steel,
    ...tirePriceTables.passenger.alloy,
  ].map((b) => ({ title: b.label, price: `${b.priceEur}€` }));

  const vanRows = [
    ...tirePriceTables.vanSuv.steel,
    ...tirePriceTables.vanSuv.alloy,
  ].map((b) => ({ title: b.label, price: `${b.priceEur}€` }));

  const cards: PriceCard[] = [
    {
      id: 'general',
      label: site.priceTable.general,
      tags: ['tunnitasu', 'fikseeritud'],
      rows: generalRows,
    },
    {
      id: 'passenger',
      label: 'Rehvivahetus',
      tags: ['sõiduautod'],
      rows: passengerRows,
    },
    {
      id: 'van',
      label: 'Rehvivahetus',
      tags: ['kaubikud', 'maasturid'],
      rows: vanRows,
    },
  ];

  const updateActiveFromScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const slides = track.querySelectorAll<HTMLElement>(`[data-slide]`);
    if (!slides.length) return;

    const trackRect = track.getBoundingClientRect();
    const centerX = trackRect.left + trackRect.width / 2;
    let best = 0;
    let bestDist = Infinity;

    slides.forEach((slide, i) => {
      const r = slide.getBoundingClientRect();
      const mid = r.left + r.width / 2;
      const dist = Math.abs(mid - centerX);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });

    setActiveIndex(best);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    updateActiveFromScroll();
    track.addEventListener('scroll', updateActiveFromScroll, { passive: true });
    window.addEventListener('resize', updateActiveFromScroll, { passive: true });
    return () => {
      track.removeEventListener('scroll', updateActiveFromScroll);
      window.removeEventListener('resize', updateActiveFromScroll);
    };
  }, [updateActiveFromScroll]);

  const goTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const slide = track.querySelector<HTMLElement>(
      `[data-slide="${index}"]`,
    );
    if (!slide) return;
    const trackRect = track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const delta =
      slideRect.left +
      slideRect.width / 2 -
      (trackRect.left + trackRect.width / 2);
    track.scrollBy({ left: delta, behavior: 'smooth' });
    setActiveIndex(index);
  };

  return (
    <section
      id="hinnakiri-cards"
      className="section"
      aria-labelledby="price-table-title"
    >
      <div className="container">
        <header className="section__header">
          <div>
            <h2
              id="price-table-title"
              className="section__title section__title--tight"
            >
              {site.priceTable.title}
            </h2>
            <p className="section__lead" style={{ marginTop: '0.5rem' }}>
              {site.priceTable.lead}
            </p>
          </div>
        </header>
      </div>

      {/* Mobile: full-bleed swipe track; desktop: constrained grid */}
      <div className={styles.carouselOuter}>
        <div
          ref={trackRef}
          className={styles.track}
          role="region"
          aria-roledescription="carousel"
          aria-label={site.priceTable.title}
        >
          {cards.map((card, index) => (
            <article
              key={card.id}
              className={styles.card}
              data-slide={index}
              aria-label={`${card.label} (${index + 1} / ${cards.length})`}
            >
              <header className={styles.cardHead}>
                <h3 className={styles.cardTitle}>{card.label}</h3>
                <ul className={styles.tags} aria-label="Kategooriad">
                  {card.tags.map((tag) => (
                    <li key={tag} className={styles.tag}>
                      {tag}
                    </li>
                  ))}
                </ul>
              </header>

              <ul className={styles.list}>
                {card.rows.map((row) => (
                  <li key={`${card.id}-${row.title}`} className={styles.row}>
                    <span className={styles.rowTitle}>{row.title}</span>
                    <span className={styles.rowPrice}>{row.price}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        <div className={styles.dots} role="tablist" aria-label="Hinnakiri kaardid">
          {cards.map((card, index) => (
            <button
              key={card.id}
              type="button"
              role="tab"
              aria-selected={activeIndex === index}
              aria-label={`${card.label}, kaart ${index + 1}`}
              className={[
                styles.dot,
                activeIndex === index ? styles.dotActive : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => goTo(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
